import type { AxiosPromise } from 'axios';
import { ApiAxiosInstance } from '../instances/apiAxiosInstance';

// TYPES
import type { ITermsConditions } from '@/shared/onboardingForms/types/termsCondition.type';
import type { AuthHeader } from '@/types/Onboarding';
import type { FilesMiniOnboardingResponse } from '@/modules/miniOnboarding/types/miniOnboarding.type';
import type { QuestionLRForm } from '@/types/LegalRepreFormType';
import type {
  ICustomerPhoneInformation,
  IPersonalInformation,
} from '@/shared/onboardingForms/types/personalInformation.type';
import type { IPurposeList } from '@/shared/onboardingForms/types/companyPurposes.type';

// DATA
import type { PayloadUserStatus } from '@/modules/team/types/users.type';
import type { CompanyInformation } from '@/types/CompanyInformationType';

export const onboardingClient = {
  /**
   * @deprecated Usar onboardingService.getLegalRepresentantQuestion() de @/shared/common/services/onboarding.service
   */
  getLegalRepresentantQuestion() {
    return ApiAxiosInstance.get<QuestionLRForm>(
      'company/b2b/representatives/joint-admin'
    );
  },

  /**
   * @deprecated Usar userInfoService.getUserInfo() de @/shared/userInfo/services/userInfo.service
   */
  getUserInfo() {
    return ApiAxiosInstance.get('company/b2b/users');
  },

  /**
   * @deprecated Usar companyService.getCompanyInformation() de @/shared/common/services/company.service
   */
  getCompanyInformation() {
    return ApiAxiosInstance.get<CompanyInformation>(
      'company/b2b/identification-address'
    );
  },

  /**
   * @deprecated Usar onboardingService.savePersonalInformationOnboarding() de @/modules/onboarding/shared/services/onboarding.service
   */
  savePersonalInformation(
    dataPersonal: IPersonalInformation,
    params?: { origin: string }
  ) {
    return ApiAxiosInstance.put<IPersonalInformation>(
      'company/b2b/users',
      dataPersonal,
      {
        params,
      }
    );
  },

  /**
   * @deprecated Usar onboardingService.updateCustomerPhone() de @/shared/common/services/onboarding.service
   */
  updateCustomerPhone(dataPersonal: ICustomerPhoneInformation) {
    return ApiAxiosInstance.put('company/b2b/users/phone', dataPersonal);
  },

  /**
   * @deprecated Usar documentsService.postCompanyFiles() de @/shared/common/services/documents.service
   */
  postCompanyFiles(
    formData: FormData,
    onUploadProgress: (progress) => void
  ): AxiosPromise {
    return ApiAxiosInstance.post('company/b2b/documents', formData, {
      onUploadProgress,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  /**
   * @deprecated Usar documentsService.getCompanyDocuments() de @/shared/common/services/documents.service
   */
  getCompanyFiles() {
    return ApiAxiosInstance.get('company/b2b/documents');
  },

  /**
   * @deprecated Usar termsService.getCompanyTerms() de @/shared/common/services/terms.service
   */
  getTermsConditions(countryCode: string) {
    return ApiAxiosInstance.get<ITermsConditions[]>(
      `company/b2b/terms/${countryCode}`
    );
  },

  /**
   * @deprecated Usar termsService.postCompanyTerms() de @/shared/common/services/terms.service
   */
  postTermsConditions() {
    return ApiAxiosInstance.post(`company/b2b/terms`);
  },

  /**
   * @deprecated Usar purposeService.getExportPurposes() de @/shared/common/services/purpose.service
   */
  getExportPurposes(countryCode: string) {
    return ApiAxiosInstance.get<IPurposeList[]>(
      `company/b2b/purpose/active-purposes/${countryCode}`
    );
  },

  /**
   * @deprecated Usar onboardingService.postOnboardingValidationPhone() de @/shared/common/services/onboarding.service
   */
  postOnboardingValidationPhone(data: AuthHeader) {
    return ApiAxiosInstance.post(`company/b2b/users/phone/validate`, null, {
      headers: {
        authType: data?.authType,
        authValue: data?.authValue || '',
      },
    });
  },

  /**
   * @deprecated Usar onboardingDocumentsService.getFilesMiniOnboarding() de @/shared/common
   */
  getFilesMiniOnboarding() {
    return ApiAxiosInstance.get<FilesMiniOnboardingResponse>(
      'company/b2b/onboarding/documents/invitations'
    );
  },

  /**
   * @deprecated Usar teamService.updateTeamUserStatus() de @/modules/team/services/team.service
   */
  patchUserStatus(payload: PayloadUserStatus, header: AuthHeader) {
    return ApiAxiosInstance.patch(
      `company/b2b/users/${payload.userId}/status`,
      {
        userStatus: payload.userStatus,
      },
      {
        headers: {
          authType: header?.authType,
          authValue: header?.authValue || '',
        },
      }
    );
  },
};
